import React, { useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useInfiniteRange, ICalendarMethods, ICalendarOptions, Days } from '@techminds/react-calendar';
import classNames from 'classnames/bind';
import { dayTexts } from '@pkgtour-workspace/common/dist/constants/date';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

import styles from './Calendar.module.scss';
import { useHolidays } from '@/hooks/useHolidays';

const cx = classNames.bind(styles);

dayjs.extend(minMax);

export interface CalendarRef {
  submit(): Days[] | void;
  reset(): void;
}

interface CalendarProps {
  defaultValue?: Days[];
  onDayClick?: (dates: Days[][]) => void;
}

const Calendar = forwardRef<CalendarRef, CalendarProps>(({ defaultValue, onDayClick }, ref) => {
  const Calendar = useInfiniteRange();
  const holidays = useHolidays();

  const [selectedDates, setSelectedDates] = useState<Days[][]>(defaultValue ? [defaultValue] : []);

  const calendarMethods = useMemo<ICalendarMethods>(
    () => ({
      onSuccess: (dates: Days[][]) => {
        onDayClick?.(dates);
        setSelectedDates(dates);
      },
      onFailure: (error: unknown) => {
        console.error(error);
      },
    }),
    [onDayClick],
  );

  const defaultStartDate = dayjs().add(14, 'day');

  const startDate = defaultValue?.[0]
    ? dayjs.min(dayjs(defaultValue?.[0]?.toDate()), defaultStartDate)
    : defaultStartDate;
  const maxMonth = dayjs().add(12, 'month').diff(startDate, 'month') + 1;
  const calendarMutableOptions = useMemo<ICalendarOptions>(
    () => ({
      selectedDates,
      holidays,
      visibleMonthCount: maxMonth,
    }),
    [selectedDates, holidays, maxMonth],
  );

  useImperativeHandle(
    ref,
    () => ({
      submit() {
        const [dates] = selectedDates;
        const [startDate, endDate] = dates || [];

        if (startDate && endDate) {
          return dates;
        }
      },
      reset() {
        setSelectedDates([]);
      },
    }),
    [selectedDates],
  );

  const calendarOptions = {
    dateFormat: 'YYYY.MM.DD.',
    todayText: '오늘',
    todaySelectable: false,
    maxMonths: 12,
    height: '100%',
    maxSelection: 1,
    startDate: startDate?.format('YYYY-MM-DD'),
    // maxRange: 31,
    allowSame: false,
    dayTexts: dayTexts,
    selectionTextBuilder: () => '',
  };

  return (
    <div className={cx('Calendar_content')}>
      <Calendar options={calendarOptions} methods={calendarMethods} mutableOptions={calendarMutableOptions} />
    </div>
  );
});

export default Calendar;
